import { useEffect, useState } from "preact/hooks";
import type { Api } from "../api.js";
import { texts, useLang } from "../i18n.js";
import { navigate } from "../router.js";
import {
  Callout,
  ConfirmDialog,
  LoadError,
  Loading,
  MoreOptions,
  PageHead,
} from "../ui.js";

/**
 * Settings › What's new (owner 2026-10-31): the Hub's changelog, one version per day, newest first. Everyone sees the
 * changes for their roles; an admin sees every role's, with a filter per role. Read from the same entries as the
 * "New for you" popup (api releases.ts), so nothing is written twice.
 */
export interface ChangelogView {
  admin: boolean;
  roles: { key: string; label: string }[];
  versions: {
    version: string;
    date: string;
    items: {
      key: string;
      title: string;
      text: string;
      roles: string[];
      forRoles: string;
      minor: boolean;
      off?: string | null;
    }[];
  }[];
}

/** Versions (owner 2026-11-03): every deploy, and the admin's choice to run an older one. api services/deploys.ts */
export interface DeploysView {
  configured: boolean;
  live: string | null;
  pin: { sha: string; at: string; by: string; byName: string } | null;
  previous: string | null;
  run: {
    status: string;
    conclusion: string | null;
    url: string;
    at: string;
  } | null;
  deploys: {
    sha: string;
    short: string;
    at: string;
    pr: number | null;
    changes: string[];
    title: string | null;
    live: boolean;
    newest: boolean;
    blocked: string | null;
  }[];
}

const T = texts(
  {
    title: "What's new",
    sub: "Changes in KFF Hub for your role, newest first.",
    subAdmin: "Every change in KFF Hub, newest first. Filter by role.",
    all: "All roles",
    roles: "Roles",
    none: "Nothing for this role yet.",
    forRoles: (w: string) => `For: ${w}`,
    back: "Go back to the previous version",
    choose: "Choose version",
    pick: "Choose",
    running: "running now",
    minorFixes: "Smaller fixes",
    pinned: (when: string, since: string, by: string) =>
      `The Hub runs the version from ${when}, since ${since} (${by}). Merges are not deployed until you go to the latest.`,
    latest: "Go to the latest",
    notSet: "Going back needs a GitHub key first (see the deploy runbook).",
    none2: "No versions recorded yet: the next deploy is the first.",
    confirmTitle: "Go back to an older version?",
    confirmBody: (when: string, gone: string) =>
      `The Hub (app and web) goes back to the version from ${when}. ${gone ? `These go away until you go to the latest again: ${gone}. ` : ""}Data people have made stays, and so do mails already sent.`,
    confirmGo: "Go back",
    latestTitle: "Go to the latest version?",
    latestBody:
      "The Hub goes to the newest version again, and merges are deployed as usual.",
    runNow: "Switching version…",
    runFailed: "The last switch failed.",
    runOk: "The last switch went through.",
    openRun: "Open in GitHub",
    turnOff: "Turn the feature off",
    time: (iso: string) =>
      new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Stockholm",
      }).format(new Date(iso)),
    date: (iso: string) =>
      new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(`${iso}T12:00:00Z`)),
  },
  {
    title: "Vad är nytt",
    sub: "Ändringar i KFF Hub för din roll, de senaste först.",
    subAdmin: "Alla ändringar i KFF Hub, de senaste först. Filtrera per roll.",
    all: "Alla roller",
    roles: "Roller",
    none: "Inget för den här rollen än.",
    forRoles: (w: string) => `För: ${w}`,
    back: "Gå tillbaka till förra versionen",
    choose: "Välj version",
    pick: "Välj",
    running: "körs nu",
    minorFixes: "Mindre rättningar",
    pinned: (when: string, since: string, by: string) =>
      `Hubben kör versionen från ${when} sedan ${since} (${by}). Nya ändringar deployas inte förrän du går till senaste.`,
    latest: "Gå till senaste",
    notSet:
      "För att kunna gå tillbaka behövs först en GitHub-nyckel (se driftmanualen).",
    none2: "Inga versioner sparade än: nästa deploy blir den första.",
    confirmTitle: "Gå tillbaka till en äldre version?",
    confirmBody: (when: string, gone: string) =>
      `Hubben (app och webb) går tillbaka till versionen från ${when}. ${gone ? `Det här försvinner tills du går till senaste igen: ${gone}. ` : ""}Data som skapats ligger kvar, och mejl som redan skickats är skickade.`,
    confirmGo: "Gå tillbaka",
    latestTitle: "Gå till senaste versionen?",
    latestBody:
      "Hubben går till den nyaste versionen igen, och nya ändringar deployas som vanligt.",
    runNow: "Byter version…",
    runFailed: "Senaste bytet misslyckades.",
    runOk: "Senaste bytet gick igenom.",
    openRun: "Öppna i GitHub",
    turnOff: "Stäng av funktionen",
    time: (iso: string) =>
      new Intl.DateTimeFormat("sv-SE", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Stockholm",
      }).format(new Date(iso)),
    date: (iso: string) =>
      new Intl.DateTimeFormat("sv-SE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(`${iso}T12:00:00Z`)),
  },
);

export function Changelog(p: { api: Api }) {
  const t = T[useLang()];
  const [v, setV] = useState<ChangelogView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [role, setRole] = useState("");
  useEffect(() => {
    p.api.get<ChangelogView>("/api/releases/changelog").then(setV, setErr);
  }, []);
  if (err) return <LoadError error={err} />;
  if (!v) return <Loading label={t.title} />;
  const versions = v.versions
    .map((x) => ({
      ...x,
      items: role ? x.items.filter((i) => i.roles.includes(role)) : x.items,
    }))
    .filter((x) => x.items.length);
  return (
    <div class="screen">
      <PageHead title={t.title} sub={v.admin ? t.subAdmin : t.sub} />
      {v.admin ? <Versions api={p.api} /> : null}
      {v.admin ? (
        <div class="chips cl-roles" role="group" aria-label={t.roles}>
          <button
            type="button"
            class="chip"
            aria-pressed={!role}
            onClick={() => setRole("")}
          >
            {t.all}
          </button>
          {v.roles.map((r) => (
            <button
              key={r.key}
              type="button"
              class="chip"
              aria-pressed={role === r.key}
              onClick={() => setRole(role === r.key ? "" : r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      ) : null}
      {versions.length ? (
        <ol class="cl-list">
          {versions.map((x) => (
            <li key={x.version} class="blk cl-version">
              <h2 class="cl-h">
                {x.version} <span class="small muted">{t.date(x.date)}</span>
              </h2>
              <ul class="cl-items">
                {x.items.map((i) => (
                  <li
                    key={i.key}
                    class={i.minor ? "cl-item cl-item--minor" : "cl-item"}
                  >
                    <span class="cl-title">{i.title}</span>
                    <span class="small">{i.text}</span>
                    {v.admin && i.forRoles ? (
                      <span class="small muted">{t.forRoles(i.forRoles)}</span>
                    ) : null}
                    {i.off ? (
                      <a
                        class="small"
                        href={i.off}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(i.off!);
                        }}
                      >
                        {t.turnOff}
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      ) : (
        <p class="muted">{t.none}</p>
      )}
    </div>
  );
}

/**
 * Admin only: "Go back to the previous version" and, under More options, every recent deploy to choose from. Nothing
 * happens by itself (owner 2026-11-03): the admin chooses, sees what goes away, and goes to the latest when ready.
 */
function Versions(p: { api: Api }) {
  const t = T[useLang()];
  const [v, setV] = useState<DeploysView | null>(null);
  const [ask, setAsk] = useState<{ sha: string } | "latest" | null>(null);
  const load = () =>
    p.api.get<DeploysView>("/api/admin/deploys").then(setV, () => setV(null));
  useEffect(() => {
    void load();
  }, []);
  // while a switch runs, look again every 15 s (the page answers from the version that runs now)
  const running = v?.run && v.run.status !== "completed";
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => void load(), 15000);
    return () => clearInterval(id);
  }, [running]);
  if (!v) return null;
  const nameOf = (d: DeploysView["deploys"][number]) =>
    d.changes.length
      ? d.changes.join(", ")
      : d.title
        ? d.title.replace(/\s*\(#\d+\)\s*$/, "")
        : t.minorFixes;
  const target =
    ask && ask !== "latest" ? v.deploys.find((d) => d.sha === ask.sha) : null;
  const liveAt = v.deploys.findIndex((d) => d.live);
  const gone = target
    ? v.deploys
        .slice(liveAt < 0 ? 0 : liveAt, v.deploys.indexOf(target))
        .map(nameOf)
        .join(" · ")
    : "";
  const pinnedRow = v.pin ? v.deploys.find((d) => d.sha === v.pin!.sha) : null;
  return (
    <div class="stack cl-versions">
      {v.pin ? (
        <Callout
          tone="warn"
          title={t.pinned(
            pinnedRow ? t.time(pinnedRow.at) : v.pin.sha.slice(0, 7),
            t.time(v.pin.at),
            v.pin.byName,
          )}
        >
          <button
            type="button"
            class="btn btn-sm"
            disabled={!v.configured || Boolean(running)}
            onClick={() => setAsk("latest")}
          >
            {t.latest}
          </button>
        </Callout>
      ) : null}
      {v.run &&
      (running || v.run.conclusion !== "success") &&
      Date.now() - Date.parse(v.run.at) < 86400000 ? (
        <p class="small">
          {running
            ? t.runNow
            : v.run.conclusion === "success"
              ? t.runOk
              : t.runFailed}{" "}
          <a href={v.run.url} target="_blank" rel="noopener noreferrer">
            {t.openRun}
          </a>
        </p>
      ) : null}
      {!v.configured ? <p class="small muted">{t.notSet}</p> : null}
      {v.previous && !v.pin ? (
        <div>
          <button
            type="button"
            class="btn"
            disabled={!v.configured || Boolean(running)}
            onClick={() => setAsk({ sha: v.previous! })}
          >
            {t.back}
          </button>
        </div>
      ) : null}
      <MoreOptions id="versions">
        <p class="small muted">{t.choose}</p>
        {v.deploys.length ? (
          <ul class="cl-deploys">
            {v.deploys.map((d) => (
              <li key={d.sha} class="cl-deploy">
                <span class="small muted">
                  {t.time(d.at)}
                  {d.pr ? ` · #${d.pr}` : ""}
                </span>
                <span>{nameOf(d)}</span>
                {d.live ? (
                  <span class="small muted">({t.running})</span>
                ) : (
                  <button
                    type="button"
                    class="btn btn-sm"
                    title={d.blocked ?? undefined}
                    disabled={
                      !v.configured || Boolean(d.blocked) || Boolean(running)
                    }
                    onClick={() =>
                      d.newest ? setAsk("latest") : setAsk({ sha: d.sha })
                    }
                  >
                    {d.newest ? t.latest : t.pick}
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p class="small muted">{t.none2}</p>
        )}
      </MoreOptions>
      {target ? (
        <ConfirmDialog
          title={t.confirmTitle}
          body={<p>{t.confirmBody(t.time(target.at), gone)}</p>}
          confirmLabel={t.confirmGo}
          danger
          onConfirm={async () => {
            await p.api.post("/api/admin/deploys/restore", { sha: target.sha });
            await load();
          }}
          onClose={() => setAsk(null)}
        />
      ) : null}
      {ask === "latest" ? (
        <ConfirmDialog
          title={t.latestTitle}
          body={<p>{t.latestBody}</p>}
          confirmLabel={t.latest}
          onConfirm={async () => {
            await p.api.post("/api/admin/deploys/latest");
            await load();
          }}
          onClose={() => setAsk(null)}
        />
      ) : null}
    </div>
  );
}
